import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProjectsHeader from '../components/ProjectsHeader';
import NavigationMenu from '../components/NavigationMenu';
import MainContentArea from '../components/MainContentArea';
import HelpDocsPage from '../pages/HelpDocsPage';
import HelpChatPage from '../pages/HelpChatPage';
import AiAdminTestsPage from '../pages/AiAdminTestsPage';
import SystemLogsPage from '../pages/SystemLogsPage';
import ProductHelpEmbeddingBanner from '../components/ProductHelpEmbeddingBanner';
import { useNotifications } from '../contexts/NotificationsContext';
import { buildMainNavLinks } from '../utils/mainNavLinks';
import '../styles/MainPage.css';
import './HelpLayout.css';

function HelpLayout() {
  const { user, loading } = useAuth();
  const { totalCount: horizonBadge, reviewCount, embeddingCount, schedulerErrorCount } = useNotifications();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('docs');

  const getInitialOpen = () => {
    try {
      const match = document.cookie.match(/(?:^|; )nav_open=([^;]*)/);
      if (match) {
        const v = decodeURIComponent(match[1]);
        return v === '1' || v === 'true';
      }
    } catch (_) {}
    return false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(getInitialOpen);
  const [disableAnim, setDisableAnim] = useState(true);

  useEffect(() => {
    const id = requestAnimationFrame(() => setDisableAnim(false));
    return () => cancelAnimationFrame(id);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const next = !prev;
      try {
        document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`;
      } catch (_) {}
      return next;
    });
  };

  const matchWithWildcard = useMatch('/help/:tab/*');
  const matchWithoutWildcard = useMatch('/help/:tab');
  const matchRoot = useMatch('/help');
  const match = matchWithWildcard || matchWithoutWildcard || matchRoot;

  useEffect(() => {
    const tab = match?.params?.tab || '';
    if (!tab) {
      navigate('/help/docs', { replace: true });
      setActiveTab('docs');
      return;
    }

    if (tab === 'system-tests' && loading) {
      if (activeTab !== 'system-tests') setActiveTab('system-tests');
      return;
    }

    const allowed = ['docs', 'chat', 'system-logs'];
    if (user?.accountType === 'admin' || user?.accountType === 'superadmin') {
      allowed.push('system-tests');
    }

    if (!allowed.includes(tab)) {
      navigate('/help/docs', { replace: true });
      setActiveTab('docs');
      return;
    }

    if (tab !== activeTab) setActiveTab(tab);
  }, [match, user, loading, navigate, activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'chat':
        return <HelpChatPage />;
      case 'system-logs':
        return <SystemLogsPage />;
      case 'system-tests':
        return <AiAdminTestsPage />;
      case 'docs':
      default:
        return <HelpDocsPage />;
    }
  };

  const linkItems = useMemo(() => buildMainNavLinks({
    user,
    horizonBadge,
    embeddingCount,
    reviewCount,
    schedulerErrorCount,
  }), [user, horizonBadge, embeddingCount, reviewCount, schedulerErrorCount]);

  return (
    <div className="help-layout">
      <ProjectsHeader isMenuOpen={isMenuOpen} onToggle={toggleMenu} title="Help" />
      <div className="help-container">
        <NavigationMenu
          isOpen={isMenuOpen}
          onToggle={toggleMenu}
          disableTransition={disableAnim}
          linkItems={linkItems}
          menuItems={[]}
          activeItem={activeTab}
          onItemClick={(id) => {
            setActiveTab(id);
            navigate(`/help/${id}`);
          }}
          showUserMenu={true}
        />
        <MainContentArea isMenuOpen={isMenuOpen} disableTransition={disableAnim}>
          <div className="help-main-content">
            <ProductHelpEmbeddingBanner />
            {renderContent()}
          </div>
        </MainContentArea>
      </div>
    </div>
  );
}

export default HelpLayout;
